import React, { useState } from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button';
import Loader from 'src/components/Loader';
import TabSwitch, { TabSwitchTab } from 'src/components/TabSwitch/TabSwitch';
import RadioButtonGroup from 'src/components/RadioButtonGroup/RadioButtonGroup';
import Callout from 'src/components/InformationCallout/Callout';
import FontAwesomeIcon from 'src/components/Icon';
import { CsvDateFormat } from 'src/services/client-types';
import './download-csv-and-pdf-parser-overlay.scss';

interface DownloadCSVAndPDFParserOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  isCSVLoading?: boolean;
  onDownloadPDF?: () => Promise<boolean>;
  isPDFLoading?: boolean;
}

interface DateFormatOption {
  value: CsvDateFormat;
  label: string;
}

type DownloadType = 'csv' | 'pdf';

const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  { value: 'UsSlash', label: 'MM/DD/YYYY (US)' },
  { value: 'Iso', label: 'YYYY-MM-DD (ISO)' },
  { value: 'EuSlash', label: 'DD/MM/YYYY (EU)' },
];

const TABS: TabSwitchTab[] = [
  { id: 'csv', label: 'CSV' },
  { id: 'pdf', label: 'PDF' },
];

export const DownloadCSVAndPDFParserOverlay = ({
  isOpen,
  onClose,
  onDownloadCSV,
  isCSVLoading = false,
  onDownloadPDF,
  isPDFLoading = false,
}: DownloadCSVAndPDFParserOverlayProps) => {
  const pdfAvailable: boolean = typeof onDownloadPDF === 'function';

  const [activeTab, setActiveTab] = useState<DownloadType>('csv');
  const [dateFormat, setDateFormat] = useState<CsvDateFormat | null>(null);

  const resetState = (): void => {
    setActiveTab('csv');
    setDateFormat(null);
  };

  const isBusy: boolean = isCSVLoading || isPDFLoading;

  const handleClose = (): void => {
    if (isBusy) {
      return;
    }

    onClose();
    resetState();
  };

  const handleDownloadCSV = async (): Promise<void> => {
    if (!dateFormat) {
      return;
    }

    const ok: boolean = await onDownloadCSV(dateFormat);

    if (ok) {
      onClose();
      resetState();
    }
  };

  const handleDownloadPDF = async (): Promise<void> => {
    if (!onDownloadPDF) {
      return;
    }

    const ok: boolean = await onDownloadPDF();

    if (ok) {
      onClose();
      resetState();
    }
  };

  const handleTabChange = (id: string): void => {
    setActiveTab(id as DownloadType);
  };

  const handleDateFormatChange = (value: string): void => {
    setDateFormat(value as CsvDateFormat);
  };

  const csvButtonDisabled: boolean = !dateFormat || isCSVLoading;
  const csvButtonLabel: string = isCSVLoading ? 'Preparing CSV...' : 'Download CSV';
  const pdfButtonLabel: string = isPDFLoading ? 'Preparing PDF...' : 'Download PDF';

  const footer = (
    <>
      <Button
        color="teal"
        label="Cancel"
        size="small"
        outlined
        onClick={handleClose}
        disabled={isBusy}
      />
      {activeTab === 'csv' ? (
        <Button
          color="teal"
          label={csvButtonLabel}
          size="small"
          onClick={handleDownloadCSV}
          disabled={csvButtonDisabled}
        />
      ) : (
        <Button
          color="teal"
          label={pdfButtonLabel}
          size="small"
          onClick={handleDownloadPDF}
          disabled={isPDFLoading}
        />
      )}
    </>
  );

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title="Download export"
      description="Choose a download type and (for CSV) a date format."
      footer={footer}
      maxWidth={'90rem'}
    >
      <div className="download-csv-and-pdf-parser-overlay">
        <div className="header-row">
          <span className="beta-badge">Beta</span>
          {pdfAvailable && (
            <TabSwitch
              tabs={TABS}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
            />
          )}
        </div>
        <div className="content">
          {isBusy ? (
            <div className="loader-wrap">
              <Loader />
            </div>
          ) : (
            <>
              {activeTab === 'csv' && (
                <>
                  <div className="file-icon">
                    <FontAwesomeIcon
                      glyph="file-csv"
                      weight="regular"
                      size="big"
                    />
                  </div>
                  <Callout
                    variant="info"
                    title="Pick a date format for your CSV"
                    subtitle="Controls how dates render inside the exported file."
                  />
                  <RadioButtonGroup
                    name="csv-date-format"
                    options={DATE_FORMAT_OPTIONS}
                    value={dateFormat ?? ''}
                    onChange={handleDateFormatChange}
                    direction="vertical"
                  />
                </>
              )}
              {activeTab === 'pdf' && (
                <>
                  <div className="file-icon">
                    <FontAwesomeIcon
                      glyph="file-pdf"
                      weight="regular"
                      size="big"
                    />
                  </div>
                  <Callout
                    variant="info"
                    title="PDF export"
                    subtitle="Download the formatted PDF report."
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </GenericDialogue>
  );
};
